import { ScreenTransitionDirection } from '../../managers/ScreenManager';
import { CardData, CardStackSocket, OptionsItemLiveData, TableData, TrickData } from '../../types/types';
import { DeclarationType, PlayerDeclaration } from '../ai/DeclarationsManager';
import { AILevel, PlayerSide, TableAI } from '../ai/TableAI';
import { Card, CardSuit } from '../card/Card';
import { TrickCollector } from '../components/TrickCollector';
import { LoaderFileKeys } from '../constant/AppConstants';
import { OptionsDataManager } from '../managers/OptionsDataManager';
import { AppDebug, AppDebugLogType } from '../debug/AppDebug';
import { AppLocalization, LanguageId } from '../localization/AppLocalization';
import { AppLocalStorage } from '../managers/AppLocalStorage';
import { AppSettings, GamePlayDirection } from '../managers/AppSettings';
import { CardsManager } from '../managers/CardsManager';
import { GTMTracking, GTMTrackingCompleteDealOrGame, GTMTrackingDealWinOrLoss } from '../managers/GTMTracking';
import { OptionsItemKeys } from '../managers/LayoutDataManager';
import { IntermediateStatsItemKeys, StatisticsDataManager } from '../managers/StatisticsDataManager';
import { Player } from '../player/Player';
import { Players, SerializableAvatarProps } from '../player/Players';
import { TableScreen, VariationId } from '../screen/TableScreen';
import { DealCompleteContentId } from '../screen/dealcomplete/DealCompleteContentScroll';
import { ContractResult, DealCompleteDealsContent } from '../screen/dealcomplete/DealCompleteDealsContent';
import { DealCompleteHandsContent } from '../screen/dealcomplete/DealCompleteHandsContent';
import { DealCompleteResultsContent } from '../screen/dealcomplete/DealCompleteResultsContent';
import { DealCompleteTricksContent } from '../screen/dealcomplete/DealCompleteTricksContent';
import { GameCompleteViewLayout } from '../screen/dealcomplete/GameCompleteView';
import { ResultsListItemBubbleColor, ResultsListItemData } from '../screen/dealcomplete/ResultsListItem';
import { GetCardSuitIconTextureStatesData, TrumpIconType } from '../utils/GetCardSuitIconTextureData';
import { GetCardsValue } from '../utils/GetCardsValue';
import { GetContractResult } from '../utils/GetContractResult';
import { GetOppositePlayerSide } from '../utils/GetOppositePlayerSide';
import { GetPlayerSide } from '../utils/GetPlayerSide';
import { TrumpSuitToCardSuit } from '../utils/TrumpsSuitToCardSuit';
import { AppTimings } from '../data/AppConstants';

/**
 * Saved as part of data model (session restore), so we provide exact values
 * (adding more wont break the old model)
 *
 * Game modes:
 * - Spades|CLUBS|DIAMONDS|HEARTS = Trump&Other
 * - NOTRUMPS = No Trumps
 * - ALLTRUMPS = All Trumps
 */
export enum TrumpSuit {
	UNDEFINED = -1,
	SPADES = 0, // symbol: ♠
	CLUBS = 1, // symbol: ♣
	DIAMONDS = 2, // symbol: ♦
	HEARTS = 3, // symbol: ♥
	NOTRUMPS = 4,
	ALLTRUMPS = 5,
}

export enum TablePhase {
	BIDDING,
	PLAY,
}

/**
 * Shared across diff. states (but not saving across those states)
 * NOTE: Check across inheritance levels too
 */
export type TableStateMachineDynamicData = {
	dealWinningSide?: PlayerSide;
	dealTotalScoreUs?: number;
	dealTotalScoreThem?: number;
	dealCompleteShowing?: boolean;
	enteredFromLobby?: boolean;
	restoreFromCache?: boolean;
};

/**
 * Call once (only init and build default internal structures):
 * - Init
 * - Build
 * Call repeatedly to 'rebuild' table state from data model:
 * - SetState
 */
export class TableStateMachine {
	public table: TableScreen;
	protected data: TableData;
	// what variation is this statemachine for
	public variationId: VariationId;

	// data computed and cached for just a split second shareable between
	// DealComplete and GameComplete screens. There are no session restore points,
	// so instead we store it here.
	protected dynamicData: TableStateMachineDynamicData;

	/**
	 * Initialize, only internals (fresh empty)
	 * - has nothing to do with actual table state/data model
	 * - runs once ever
	 */
	public Init(table: TableScreen): void {
		this.table = table;

		this.dynamicData = {};
	}

	public SetRestoredFromCache(restoredFromCache: boolean): void {
		this.dynamicData.restoreFromCache = restoredFromCache;
	}

	protected SetDynamicDataDefaults(restoredFromCache: boolean = false, enteredFromLobby: boolean = false): void {
		this.dynamicData.dealWinningSide = null;
		this.dynamicData.dealTotalScoreUs = 0;
		this.dynamicData.dealTotalScoreThem = 0;
		// if DealsCompleteScreen is about to show
		this.dynamicData.dealCompleteShowing = this.data.historyTricksData.length === 7 && this.data.playStackCards.length === 4;
		this.dynamicData.enteredFromLobby = enteredFromLobby;
		this.dynamicData.restoreFromCache = restoredFromCache;
	}

	public GetDynamicDataDealCompleteShowing(): boolean {
		return this.dynamicData.dealCompleteShowing;
	}

	public GetData(): TableData {
		return this.data;
	}

	public GetDataNewGame(variationId: number): TableData {
		// console.log("GetDataNewGame",VariationId[variationId]);
		const gamePlayDirection: GamePlayDirection = OptionsDataManager.GetGameplayDirection();
		let trickDealerIndex = Players.PLAYER_INDEX;
		// DEBUG
		// trickDealerIndex = 3;
		const playersAvatarsProps: SerializableAvatarProps[] = this.table.players.GenerateAvatarsProps();
		// get all cards, shuffled, from cards manager into init stack as CardData[]
		const allCardsData: CardData[] = Phaser.Utils.Array.Shuffle(CardsManager.GetPlayableCardDatas());
		const aiLevel: AILevel = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.aiLevel, variationId).value as AILevel;
		const data: TableData = {
			variationId: variationId,
			seed: Math.random(),
			playersAvatarsProps: playersAvatarsProps,
			initStackCards: allCardsData,
			playStackCards: [],
			playersCardsData: [[], [], [], []],
			options: {
				gamePlayDirection: gamePlayDirection,
				aiLevel: aiLevel,
			},
			dealDealerIndex: trickDealerIndex,
			trickDealerIndex: trickDealerIndex,
			// dealer does not play
			// this is the first task's player index
			lastPlayedPlayerIndex: this.GetFixedPlayerIndex(trickDealerIndex + gamePlayDirection),

			// coinche, contree
			bids: {
				history: [],
				highestChoiceType: TrumpSuit.UNDEFINED,
				highestChoiceValueIndex: -1,
				highestChoicePlayerIndex: -1,
			},
			contraPlayerIndex: -1,
			recontraPlayerIndex: -1,

			playerAttackerIndex: -1,
			trumpSuit: TrumpSuit.UNDEFINED,

			curDealScoreUs: 0,
			curDealScoreThem: 0,
			numDealsWonUs: 0,
			numDealsWonThem: 0,
			dealBelotePointsUs: 0,
			dealBelotePointsThem: 0,
			dealMajorDeclarationPointsUs: 0,
			dealMajorDeclarationPointsThem: 0,
			declarations: [],

			hangingPoints: 0,
			gameTotalScoreUs: 0,
			gameTotalScoreThem: 0,

			// table data
			historyDealsData: [],
			historyTricksData: [],
			historyHandsData: [[], [], [], []],
		};
		return data;
	}

	public DeleteTableData(): void {
		this.data = null;
		AppLocalStorage.DeleteTableData(this.variationId);
	}

	public SetData(data: TableData): void {
		this.data = data;
	}

	/**
	 * Reset vars to default values
	 * To create new, @see Init
	 */
	public GetSetDataNewGame(variationId: VariationId, saveToStorage: boolean = true): void {
		this.data = this.GetDataNewGame(variationId);

		// save immediately, or, if overriding in subclass, save there after overrides
		if (saveToStorage) {
			AppLocalStorage.SetTableData(this.variationId, this.data);
		}

		// override in subclass
	}

	public SetDataNextDeal(saveToStorage: boolean = true): void {
		const gamePlayDirection: number = this.data.options.gamePlayDirection;
		const dealDealerIndex = this.GetFixedPlayerIndex(this.data.dealDealerIndex + gamePlayDirection);
		const nextPlayedPlayerIndex: number = this.GetFixedPlayerIndex(dealDealerIndex + gamePlayDirection);
		// get all cards, shuffled, from cards manager into init stack as CardData[]
		const allCardsData: CardData[] = Phaser.Utils.Array.Shuffle(CardsManager.GetPlayableCardDatas());
		const resetData = {
			initStackCards: allCardsData,
			playStackCards: [],
			playersCardsData: [[], [], [], []],

			dealDealerIndex: dealDealerIndex,
			trickDealerIndex: dealDealerIndex,
			lastPlayedPlayerIndex: nextPlayedPlayerIndex,

			// coinche, contree
			bids: {
				history: [],
				highestChoiceType: TrumpSuit.UNDEFINED,
				highestChoiceValueIndex: -1,
				highestChoicePlayerIndex: -1,
			},
			contraPlayerIndex: -1,
			recontraPlayerIndex: -1,

			playerAttackerIndex: -1,
			trumpSuit: TrumpSuit.UNDEFINED,

			dealBelotePointsUs: 0,
			dealBelotePointsThem: 0,
			dealMajorDeclarationPointsUs: 0,
			dealMajorDeclarationPointsThem: 0,
			// hangingPoints: 0, // carries over to next deal; reset after you add them;

			//
			// gameTotalScoreUs: 0,
			// gameTotalScoreThem: 0,

			declarations: undefined,

			// table data
			// historyDealsData: [],
			historyTricksData: [],
			historyHandsData: [[], [], [], []],

			curDealScoreUs: 0,
			curDealScoreThem: 0,
		};

		this.data = {
			...this.data,
			...resetData,
		};

		// save immediately, or, if overriding in subclass, save there after overrides
		if (saveToStorage) {
			AppLocalStorage.SetTableData(this.variationId, this.data);
		}

		// override in subclass
	}

	/**
	 * Update component views and state from local data model (this.data)
	 *
	 * Local data model (this.data) has already been set to:
	 * A) default (empty/initial) state - 'New Game'
	 * B) new 'deal' state - 'New Deal'
	 * C) obscure state - 'Restore From Cache'
	 */
	public UpdateViewFromData(): void {
		// console.log("UpdateViewFromData");
		// console.log(">",VariationId[AppLocalization.GetCurVariationId()]);

		const langId: LanguageId = AppLocalization.GetCurLanguageId();
		const isContra: boolean = this.data.contraPlayerIndex != -1;
		const isRecontra: boolean = this.data.recontraPlayerIndex != -1;

		// pool all cards (no matter where they are)
		// EDIT: now during Table->Lobby transition
		// this.table.Reset();

		// console.log("CardsManager.GetLength=",CardsManager.GetLength());
		// if cards in init stack data model
		if (this.data.initStackCards.length) {
			// pick cards from cards manager
			const initStackCards: Card[] = CardsManager.PickCards(
				this.data.initStackCards,
				false // frontFacing?
			);
			// console.log(initStackCards.map(card=>card.log()))
			const cardSockets: CardStackSocket[] = initStackCards.map((card: Card, i: number) => {
				const socket: CardStackSocket = this.table.tableStackInit.GetSocket();
				return socket;
			});

			// disable hints btn and hide hint popup
			// toggle off tips button
			this.table.btnHints.SetSelected(false);
			// disable tips button
			this.table.btnHints.SetInteractive(false);
			// hide tips
			this.table.popupHint.Hide(false);

			// drop cards into init stack
			this.table.tableStackInit.DropCards(
				initStackCards,
				cardSockets,
				false // reverse order?
			);

			// if players have cards (then we are in pick/choose state)
			// console.log(this.data.playersCardsData);
			// console.log(this.data.state.taskType, this.data.state.taskSubType); // 3, 1
			// console.log(this.data.playersCardsData, this.data.state.taskType, this.data.state.taskSubType);
			// console.log(this.data.playersCardsData?.[0]?.length > 0);

			// if players have cards
			// (if this is not pre-first-dealer-to-player deal)
			if (this.data.playersCardsData?.[0]?.length > 0) {
				// flip top card
				this.table.tableStackInit.GetLast().Flip();
			}

			// show init stack
			this.table.tableStackInit.Show(false);
		}

		// if cards in play stack data model
		if (this.data.playStackCards.length) {
			const playStackCards: Card[] = CardsManager.PickCards(
				this.data.playStackCards,
				true // frontFacing?
			);
			// drop cards into init stack
			const gameplayDirection = this.data.options.gamePlayDirection;
			const cardSockets: CardStackSocket[] = playStackCards.map((card: Card, i: number) => {
				let playerIndex: number;
				if (gameplayDirection == GamePlayDirection.CCW) {
					playerIndex = this.GetFixedPlayerIndex(i - playStackCards.length + this.data.trickDealerIndex - gameplayDirection);
				}
				else {
					playerIndex = this.GetFixedPlayerIndex(this.data.trickDealerIndex + i);
				}
				const socket: CardStackSocket = this.table.tableStackPlay.GetSocket(playerIndex);
				return socket;
			});
			if (gameplayDirection == GamePlayDirection.CW) {
				cardSockets.reverse();
			}
			this.table.tableStackPlay.DropCards(playStackCards, cardSockets); // cards,,reverse?
		}

		// players
		// set player names from data or generate new
		this.table.players.SetAvatarsProps(this.data.playersAvatarsProps, langId);

		// live player acceptorBubble ('OK' to manually collect cards or collect declarations)
		this.table.players.GetPlayerByIndex(Players.PLAYER_INDEX).acceptorBubble.Hide(false);

		// if cards in players hands cards model
		if (this.data.playersCardsData.length) {
			this.data.playersCardsData.forEach((playerCardDatas: CardData[], i: number) => {
				// if there are cards for this player's hand
				if (playerCardDatas && playerCardDatas.length) {
					// console.log(playerCardDatas);
					const player: Player = this.table.players.GetPlayerByIndex(i);
					const playerCards: Card[] = CardsManager.PickCards(
						playerCardDatas,
						i == Players.PLAYER_INDEX ? true : false // frontFacing? (player=true; bot=false)
					);
					player.DropCards(
						playerCards,
						false, // animateReorder?
						TrumpSuit.UNDEFINED,
						true,
						true,
						true // skipReorder
					);
				}
			});
		}

		// tableScore
		this.table.tableScore.SetCurDealScoreUs(this.data.curDealScoreUs);
		this.table.tableScore.SetCurDealScoreThem(this.data.curDealScoreThem);
		this.table.tableScore.SetTotalScoreUs(this.data.gameTotalScoreUs);
		this.table.tableScore.SetTotalScoreThem(this.data.gameTotalScoreThem);

		if (this.data.trumpSuit != TrumpSuit.UNDEFINED) {
			// selectedTrumpView
			this.table.selectedTrumpView.SetPlayerIndex(this.data.playerAttackerIndex);
			this.table.selectedTrumpView.SetTrumpSuit(this.data.trumpSuit);
			// bid (value)
			const sBid: string = this.IsContraVariation()
				? AppLocalization.GetVariationData().table.bidPopup.slider.choicesShortText[this.data.bids.highestChoiceValueIndex]
				: null;
			this.table.selectedTrumpView.SetBidValue(sBid); // updateLayout?
			// contra|re-contra (coinche:coinche|surcoinche, contree: double|redouble)
			let sContra: string = null;
			if (isRecontra) {
				sContra = AppLocalization.GetVariationData().table.selectedTrumpView.contraShortText;
			} else if (isContra) {
				sContra = AppLocalization.GetVariationData().table.selectedTrumpView.contraShortText;
			}
			this.table.selectedTrumpView.SetContra(sContra);

			// update scoreView and selectedTrumpView (size, layout/snapping)
			// from screen scale OR visibility change
			this.table.UpdateScoreAndSelectedTrumpViews();

			const cardDeckIndex: number = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.cardDeck).value as number;
			this.table.cardPointsView.SetData(this.data.trumpSuit, cardDeckIndex);
		}

		// lastTrickView
		if (this.data.historyTricksData.length) {
			const lastTrickData: TrickData = this.data.historyTricksData[this.data.historyTricksData.length - 1];
			this.table.lastTrickView.SetData(lastTrickData);
		}

		// dealerChipController
		this.table.dealerChipController.Reset();
		this.table.dealerChipController.SetDealerIndex(this.data.dealDealerIndex);
		// this.table.dealerChipController.SnapToPlayer(this.data.dealDealerIndex);

		// override in subclass
	}

	/**
	 * Reset table component views and states to defaults,
	 * in preparation to setting them to a specific state
	 */
	protected Reset(): void {}

	/**
	 * Resume state machine, where you left off (eg if view was build from default state, start at beginning)
	 */
	public Start(restoredFromCache: boolean, enteredFromLobby: boolean): void {
		// reset dynamicData to defaults
		this.SetDynamicDataDefaults(restoredFromCache, enteredFromLobby);

		// trace table data for debug mode
		AppDebug.log(
			[`%c${VariationId[this.variationId]} / TSM::Start / tableData:`, 'color:orange;font-weight:bold;', `${JSON.stringify(this.data)}`],
			AppDebugLogType.DEBUG
		);

		const duration = this.GetStartDelay(restoredFromCache, enteredFromLobby);
		if (duration > 0) {
			this.table.scene.time.delayedCall(duration, () => {
					// console.log(this.data.state.taskType, this.data.state.taskData);
				
					this.TaskStart(this.data.state.taskType, this.data.state.taskData);
			});
		}
		else {
			// dont create delayedCall Event
			this.TaskStart(this.data.state.taskType, this.data.state.taskData);
		}

		// override in subclass
	}

	protected GetStartDelay(restoredFromCache: boolean, enteredFromLobby: boolean): number {
		const screenShowDuration = OptionsDataManager.GetTiming(AppTimings.screenShowDuration);
		let duration: number = 0;

		// console.log(
		// 	this.dynamicData.dealCompleteShowing,
		// 	restoredFromCache,
		// 	enteredFromLobby
		// );
		
		// cache (dealCompleteScreen)
		if (this.dynamicData.dealCompleteShowing && restoredFromCache && !enteredFromLobby) {
			duration = 0;
		}
		else
		// cache (table)
		if (!this.dynamicData.dealCompleteShowing && restoredFromCache && !enteredFromLobby) {
			duration = screenShowDuration;
		}
		else
		// Lobby->New Game
		if (!this.dynamicData.dealCompleteShowing && !restoredFromCache && enteredFromLobby) {
			duration = 0;
		}
		else
		// Lobby->Continue Game
		if (!this.dynamicData.dealCompleteShowing && !restoredFromCache && enteredFromLobby) {
			duration = screenShowDuration;
		}
		return duration;
	}

	public TaskStart(taskType: number, taskData: any = null): void {
		// override in subclass
	}

	/**
	 * NOTE: for exact rules for (Un)Announced Capot/Generale, please refer to doc:
	 *       'Finalizing remaining rules / texts / math etc' or
	 *       https://docs.google.com/document/d/1qcSuUjjtDowMB6c5SpdQzfSn7PcyTNivEeHetggVPfg
	 */
	protected StartCollectCards(taskType: number): void
	{
		const animate = !this.dynamicData.dealCompleteShowing;
		const playDirection: GamePlayDirection = this.data.options.gamePlayDirection;
		const tableAI: TableAI = this.table.GetTableAI();
		const trickWinnerIndex: number = tableAI.GetTrickWinner().playerIndex;
		const trickWinnerSide: PlayerSide = GetPlayerSide(trickWinnerIndex);
		// console.log("trickWinnerSide",PlayerSide[trickWinnerSide]);
		const trickDataCards: CardData[] = [];
		const tableCards: Card[] = this.table.tableStackPlay.GetCards();
		const isLastTrick: boolean = this.table.players.GetPlayerByIndex(0).GetCards().length == 0;

		tableCards.forEach((card, i) => {
			const cardData: CardData = { suit: card.suit, rank: card.rank };
			const cardFixedIndex = this.GetFixedPlayerIndex(this.data.trickDealerIndex + i * playDirection);
			trickDataCards[cardFixedIndex] = cardData;
		});
		const trickData: TrickData = {
			cards: trickDataCards,
			winnerIndex: trickWinnerIndex,
		};
		this.data.historyTricksData.push(trickData);
		this.table.lastTrickView.SetData(trickData);
		if (!this.table.lastTrickView.IsShowing()) {
			this.table.lastTrickView.Show(animate);
		}

		// add trick value to winning team, and if this is last trick collection,
		// resolve dixdeder, capot, GENERALE (if applicable)
		// let isContractCapot: boolean;
		// let isContractGenerale: boolean;
		let isCapot: boolean;
		// let isGenerale: boolean;
		const trickValue: number = GetCardsValue(this.table.tableStackPlay.GetCards(), this.data.trumpSuit, this.table.variationId);
		switch (trickWinnerSide) {
			// we won last trick
			case PlayerSide.US:
				this.data.curDealScoreUs += trickValue; // total from tricks: 152
				if (isLastTrick) {
					this.data.curDealScoreUs += 10; // dixDeDer: 10
					// NOW (after all tricks have been assigned) calc CAPOT
					isCapot = this.IsCapot();
					// if we made Capot (announced or unannounced - true for both cases)
					// NOTE: this also runs for Generale (announced or unannounced)
					if (isCapot) {
						this.data.curDealScoreUs += 90; // additional from Capot: 90
					}
				}
				this.table.tableScore.SetCurDealScoreUs(this.data.curDealScoreUs);
				break;

			case PlayerSide.THEM:
				this.data.curDealScoreThem += trickValue;
				if (isLastTrick) {
					this.data.curDealScoreThem += 10;
					// NOW (after all tricks have been assigned) calc CAPOT
					isCapot = this.IsCapot();
					if (isCapot) {
						this.data.curDealScoreThem += 90;
					}
				}
				this.table.tableScore.SetCurDealScoreThem(this.data.curDealScoreThem);
				break;
		}

		const showScores: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.showScores, this.variationId);
		if (showScores && showScores.value) {
			if (!this.table.tableScore.IsShowing()) {
				this.table.tableScore.Show(animate);
			}
		}

		// update scoreView and selectedTrumpView (size, layout/snapping)
		// from screen scale OR visibility change
		this.table.UpdateScoreAndSelectedTrumpViews();

		this.data.trickDealerIndex = trickWinnerIndex;
		this.data.lastPlayedPlayerIndex = trickWinnerIndex;

		// collect cards
		const trickCollector: TrickCollector = this.table.components.GetComponent(this.table, TrickCollector);
		trickCollector.SetData({
			taskType: taskType,
			onComplete: this.OnTaskComplete.bind(this),
			playerIndex: trickWinnerIndex,
		});
		trickCollector.Start(animate);
	}

	/**
     * Base implementation of StartTask#COMPLETE_DEAL. This implementation is the same for each variation
     * 
     * 
        DealComplete screen items (ORDER)
        ---------------------------------
        Trump
        COINCHE {
            Contract
            Contra / Recontra
        }
        Points done (Table tricks + dixdeder + {capot})
        Belote (IF isBeloteCountsForContractWinLoss=TRUE)
        Declarations (IF playWithDeclarations=TRUE && isDeclarationsCountForContractWinLoss==TRUE)
        Total (Totals incl all above, specif.: belote(if applicable), declarations(if applicable), hanging, Capot|Generale)
        Result ([Done], [Failed] or [Hanging])
        Belote (IF isBeloteCountsForContractWinLoss=FALSE)
        Declarations (IF playWithDeclarations=TRUE && isDeclarationsCountForContractWinLos=s=FALSE)
        Hung (IF isUseHangingPoints=TRUE && hangingPoints != 0)
        Score (Totals incl all above)
        Total Score (Game Total)
     * 
     * @param taskType The Task ID for COMPLETE_DEAL task inside the specific variation StateMachine, eg TaskTypeCoinche.COMPLETE_DEAL for Coinche.
     */
	protected StartDealComplete(taskType: number): void {
		const useHangingPoints = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.hangingPoints, this.table.variationId);
		const isUseHangingPoints = useHangingPoints && (useHangingPoints.value as boolean);
		const beloteCountsForContractWinLoss = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.beloteCountsForContractWinLoss, this.table.variationId);
		const isBeloteCountsForContractWinLoss = beloteCountsForContractWinLoss && (beloteCountsForContractWinLoss.value as boolean);
		const declarationsCountForContractWinLoss = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.declarationsCountForContractWinLoss, this.table.variationId);
		const isDeclarationsCountForContractWinLoss = declarationsCountForContractWinLoss && (declarationsCountForContractWinLoss.value as boolean);
		const playWithDeclarations = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.playWithDeclarations, this.table.variationId);
		const isPlayWithDeclarations = playWithDeclarations && (playWithDeclarations.value as boolean);
		const contractValueSufficientForAttackWin = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.contractValueSufficientForAttackWin, this.table.variationId);
		const isContractValueSufficientForAttackWin = contractValueSufficientForAttackWin && (contractValueSufficientForAttackWin.value as boolean);
		const declarationLossOnContractLossOrCapot = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.declarationLossOnContractLossOrCapot, this.table.variationId);
		const isDeclarationLossOnContractLossOrCapot = declarationLossOnContractLossOrCapot && (declarationLossOnContractLossOrCapot.value as boolean);
		const scoreContractOnly = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.scoreContractOnly, this.table.variationId);
		const isScoreContractOnly = scoreContractOnly && (scoreContractOnly.value as boolean);
		const ntatDoublePoints = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.ntatDoublePoints, this.table.variationId);
		const isNTATDoublePoints = ntatDoublePoints && (ntatDoublePoints.value as boolean);
		const coincheDoublesAllPoints = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.coincheDoublesAllPoints,	this.table.variationId);
		const doubleDoublesAllPoints = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.doubleDoublesAllPoints,	this.table.variationId);
		let isContraDoublesAllPoints = (coincheDoublesAllPoints && (coincheDoublesAllPoints.value as boolean)) || (doubleDoublesAllPoints && (doubleDoublesAllPoints.value as boolean));

		// generic refs
		const attackingSide: PlayerSide = GetPlayerSide(this.data.playerAttackerIndex);
		const defendingSide: PlayerSide = GetOppositePlayerSide(attackingSide);
		// Is Coinche or Contree variation table.
		// const isContraVariation: boolean = this.data.bids && this.data.bids.highestChoiceValueIndex != -1;
		const isContraVariation: boolean = this.IsContraVariation();
		// const playerSide = GetPlayerSide(Players.PLAYER_INDEX);
		const isContra: boolean = this.data.contraPlayerIndex != -1;
		const isRecontra: boolean = this.data.recontraPlayerIndex != -1;
		// capot / generale flags (announced and unannounced)
		// const isContractCapot: boolean = this.IsContractCapot();
		const isContractGenerale: boolean = this.IsContractGenerale(); // generale contract was declared during bidding phase
		const isCapot: boolean = this.IsCapot(); // single team took all tricks
		const isGenerale: boolean = this.IsGenerale(); // single player took all tricks
		const belotePointsUs = this.data.dealBelotePointsUs;
		const belotePointsThem = this.data.dealBelotePointsThem;

		// major goals refs
		// EDIT: WONT calc CAPOT/GENERALE, until RESULT.
		// 1) 'Points Done': tricks only (no declarations, no belote, no hanging), includes: dixderder, Capot|Generale
		let pointsDoneUs: number = this.data.curDealScoreUs;
		let pointsDoneThem: number = this.data.curDealScoreThem;
		// 2) 'Total': (1) + Belote(beloteCountsForContractWinLoss) + Declarations(isDeclarationsCountForContractWinLoss)
		let totalUs: number = 0;
		let totalThem: number = 0;
		// Totals except belote (for adding to Score)
		// let totalNoBeloteUs: number = 0;
		// let totalNoBeloteThem: number = 0;
		// RESULT here ----------------------------------------------------------------------------------------------------
		// 3) 'Score': (2) + Belote(!beloteCountsForContractWinLoss) + Declarations(!isDeclarationsCountForContractWinLoss) + hangingPoints(...); and other toggls like 'scoreContractOnly(override) etc.
		//    ALSO: apply Capot/Generale, mults (C/SC), 'rest flags'
		let scoreUs: number = 0;
		let scoreThem: number = 0;

		const isGeneraleUs: boolean = isGenerale && pointsDoneUs > 0;
		const isGeneraleThem: boolean = isGenerale && pointsDoneThem > 0;
		// const isCapotUs: boolean = isCapot && (pointsDoneUs > 0);
		// const isCapotThem: boolean = isCapot && (pointsDoneThem > 0);

		// Coinche|Contree
		let dealContractValue: number = 0;
		let dealContractValueUs = 0;
		let dealContractValueThem = 0;
		if (isContraVariation) {
			dealContractValue = AppLocalization.GetVariationData().table.bidPopup.slider.choicesContractValue[this.data.bids.highestChoiceValueIndex];
			// our contract
			if (attackingSide == PlayerSide.US) {
				dealContractValueUs = dealContractValue;
			} 
			else 
			if (attackingSide == PlayerSide.THEM) {
				dealContractValueThem = dealContractValue;
			}
		}
		
		// cache pre-'Result' and post-'Result' declarations (major) and belote (if any) values
		let belotePointsPreTotalUs: number = 0;
		let belotePointsPreTotalThem: number = 0;
		let belotePointsPostTotalUs: number = 0;
		let belotePointsPostTotalThem: number = 0;
		if (isBeloteCountsForContractWinLoss) {
			belotePointsPreTotalUs = belotePointsUs;
			belotePointsPreTotalThem = belotePointsThem;
		} else {
			belotePointsPostTotalUs = belotePointsUs;
			belotePointsPostTotalThem = belotePointsThem;
		}
		let declarationsPointsPreTotalUs: number = 0;
		let declarationsPointsPreTotalThem: number = 0;
		let declarationsPointsPostTotalUs: number = 0;
		let declPointsPostTotalThem: number = 0;
		if (isDeclarationsCountForContractWinLoss) {
			declarationsPointsPreTotalUs = this.data.dealMajorDeclarationPointsUs;
			declarationsPointsPreTotalThem = this.data.dealMajorDeclarationPointsThem;
		} else {
			declarationsPointsPostTotalUs = this.data.dealMajorDeclarationPointsUs;
			declPointsPostTotalThem = this.data.dealMajorDeclarationPointsThem;
		}

		// (2) 'Total' (midway Deal calculations)
		// totalUs & totalThem
		totalUs = pointsDoneUs + belotePointsPreTotalUs + declarationsPointsPreTotalUs;
		totalThem = pointsDoneThem + belotePointsPreTotalThem + declarationsPointsPreTotalThem;
		// totalNoBeloteUs = pointsDoneUs + declarationsPointsPreTotalUs;
		// totalNoBeloteThem = pointsDoneThem + declarationsPointsPreTotalThem;

		// [[ dealwinningSide ]]
		// if Coinche|Contree
		if (isContraVariation) {
			// is reaching CONTRACT score enough to fulfill contract?
			// Coinche toggle: contractValueSufficientForAttackWin
			if (isContractValueSufficientForAttackWin) {
				// if we declared the contract
				if (attackingSide == PlayerSide.US) {
					// if we fulfilled the contract
					if (totalUs >= dealContractValue) {
						this.dynamicData.dealWinningSide = PlayerSide.US;
					} else {
						this.dynamicData.dealWinningSide = PlayerSide.THEM;
					}
				} else if (attackingSide == PlayerSide.THEM) {
					// if they fulfilled the contract
					if (totalThem >= dealContractValue) {
						this.dynamicData.dealWinningSide = PlayerSide.THEM;
					} else {
						this.dynamicData.dealWinningSide = PlayerSide.US;
					}
				}
			}
			// not contractValueSufficientForAttackWin
			else {
				// Generale override
				if (isContractGenerale) {
					// we declared generale
					if (attackingSide == PlayerSide.US) {
						// we made generale
						if (isGeneraleUs) {
							this.dynamicData.dealWinningSide = PlayerSide.US;
						}
						// we declared generale but didnt make generale, so they win
						else {
							this.dynamicData.dealWinningSide = PlayerSide.THEM;
						}
					}
					// they declared generale
					if (attackingSide == PlayerSide.THEM) {
						// they made generale
						if (isGeneraleThem) {
							this.dynamicData.dealWinningSide = PlayerSide.THEM;
						}
						// they declared but didnt make generale, so we win
						else {
							this.dynamicData.dealWinningSide = PlayerSide.US;
						}
					}
				}
				// Generale not declared (and won or not)
				else {
					// capot made (includes unannounced Generale)
					if (isCapot) {
						// we won capot
						if (pointsDoneUs > 0) {
							this.dynamicData.dealWinningSide = PlayerSide.US;
						}
						// they won capot
						else {
							this.dynamicData.dealWinningSide = PlayerSide.THEM;
						}
					}
					// nor Generale, neither Capot made
					else {
						// if we declared the contract
						if (attackingSide == PlayerSide.US) {
							// if we fulfilled the contract
							if (totalUs >= dealContractValue && totalUs > totalThem) {
								this.dynamicData.dealWinningSide = PlayerSide.US;
							} else {
								this.dynamicData.dealWinningSide = PlayerSide.THEM;
							}
						}
						// if they declared the contract
						else if (attackingSide == PlayerSide.THEM) {
							// if they fulfilled the contract
							if (totalThem >= dealContractValue && totalThem > totalUs) {
								this.dynamicData.dealWinningSide = PlayerSide.THEM;
							} else {
								this.dynamicData.dealWinningSide = PlayerSide.US;
							}
						}
					}
				}
			}
		}
		// Belote
		else {
			// Capot override
			if (isCapot) {
				if (pointsDoneUs > 0) {
					this.dynamicData.dealWinningSide = PlayerSide.US;
				} else {
					this.dynamicData.dealWinningSide = PlayerSide.THEM;
				}
			}
			// not Capot
			// Belote normal counting
			else {
				if (isUseHangingPoints) {
					if (totalUs > totalThem) {
						this.dynamicData.dealWinningSide = PlayerSide.US;
					} else if (totalUs < totalThem) {
						this.dynamicData.dealWinningSide = PlayerSide.THEM;
					} else {
						// its a deuce!
						this.dynamicData.dealWinningSide = PlayerSide.NONE;
					}
				} else {
					if (totalUs >= totalThem) {
						this.dynamicData.dealWinningSide = PlayerSide.US;
					} else {
						this.dynamicData.dealWinningSide = PlayerSide.THEM;
					}
				}
			}
		}

		// console.log(this.data.dealMajorDeclarationPointsUs, this.data.dealMajorDeclarationPointsThem);
		
		// MAJ DECL PTS CONSIDERING [ isDeclarationLossOnContractLossOrCapot ]
		// "Declarations are lost when failing a contract or being capot. Major declarations are lost"
		// Major DECL loss on contract: ON (OFF=each team takes their DECL)
		// SIDE 	CONTRACT		DECL	RESULT
		// ------------------------------------------------------
		// ATK		120/FAIL			100		0
		// DEF							100		200
		// ------------------------------------------------------
		// ATK		120/WIN			100		100
		// DEF							100		100
		// ------------------------------------------------------
		// ATK		120/UCAP WIN	100		200
		// DEF							100		0
		// ------------------------------------------------------
		// ATK		GEN/WIN			100		200 (UCAP)
		// DEF							100		0
		// ------------------------------------------------------
		// ATK		CAP/WIN			100		200
		// DEF							100		0
		// ------------------------------------------------------
		let declPointsParsedUs: number = this.data.dealMajorDeclarationPointsUs;
		let declPointsParsedThem: number = this.data.dealMajorDeclarationPointsThem;

		// overrides only apply when this is ON
		if (isDeclarationLossOnContractLossOrCapot) {
			// attackers (those who called contract) lost
			// - this includes defender's CAPOT, UCAPOT (unannounced capot) or GENERALE.
			if (attackingSide != this.dynamicData.dealWinningSide) {
				// move major declarations from attackers to defenders (from side that lost to side that won)
				if (this.dynamicData.dealWinningSide == PlayerSide.US) {
					declPointsParsedUs = this.data.dealMajorDeclarationPointsUs + this.data.dealMajorDeclarationPointsThem;
					declPointsParsedThem = 0;
				}
				else {
					declPointsParsedUs = 0;
					declPointsParsedThem = this.data.dealMajorDeclarationPointsUs + this.data.dealMajorDeclarationPointsThem;
				}
			}
			// attackers (those who called contract) won
			// - this moves DECLARATIONS from defenders to attackers only if attackers fulfilled GENERALE contract (because it includes UCAPOT) or fulfilled CAPOT contract (announced or unannounced). Due to how Capot is calculated (incl UCapot), we simply have to check for isCapot.
			else {
				// attackers won announced Capot (CAPOT) or unannounced Capot (GENERALE or UCAPOT)
				if (isCapot) {
					// move major declarations from defenders to attackers (from side that lost to side that won)
					if (this.dynamicData.dealWinningSide == PlayerSide.US) {
						declPointsParsedUs = this.data.dealMajorDeclarationPointsUs + this.data.dealMajorDeclarationPointsThem;
						declPointsParsedThem = 0;
					}
					else {
						declPointsParsedUs = 0;
						declPointsParsedThem = this.data.dealMajorDeclarationPointsUs + this.data.dealMajorDeclarationPointsThem;
					}
				}
			}
		}

		// compute contra multiplier for final scoreUs & scoreThem calculations (after this)
		// NOTE: Using 'scoreContractOnly', the contract still gets multiplied
		let contraMult: number = 1;
		if (isContra) {
			contraMult = isRecontra ? 4 : 2;
		}

		// (3) 'Score' (final Deal calculations)
		// scoreUs & scoreThem
		// Attacker wins: Each team gets their points.
		// Defender wins: Receives points of Attackers (except declarations and belote, depending on options).
		// Deuce: Defenders score their points; Attackers points HANG to next Deal, are given then to winner.

		// NOTE: Contract amount has already been assigned to dealContractValue
		// NOTE: we 'assume' here, that 'isContraDoublesAllPoints' also applies to the GENERALE's bonus 250 from silent CAPOT
		// EDIT: 0.8.5: silent capot NOT to be multiplied by contra/recontra (x2/x4)
		let silentCapotValue: number = 0;
		if (isContractGenerale) {
			// we made generale (or capot)
			if (isGenerale) {
				// add 'silent capot' 250 to Generale's base 500.
				silentCapotValue = 250;
			}
		}
		
		let hangingPointsUs: number = 0;
		let hangingPointsThem: number = 0;
		if (this.dynamicData.dealWinningSide == PlayerSide.US) {
			// we are attacking and winning
			// (they are defending and losing)
			if (this.dynamicData.dealWinningSide == attackingSide) {
				// if Coinche|Contree
				if (isContraVariation) {
					// we bid, we won
					// if scoreContractOnly
					if (isScoreContractOnly) {
						// we get: contract
						// they get: 0
						// scoreUs = dealContractValue * contraMult + silentCapotValue;
						scoreUs = dealContractValue * contraMult;
						scoreThem = 0;
					}
					// not scoreContractOnly
					else {
						// we bid, we won, got contra-ed.
						// - Contra awards all tricks to winner
						if (isContra) {
							// - we get: contract + our tricks + their tricks + our belote + our decl
							// - they get: their belote + their decl
							if (isContraDoublesAllPoints) {
								scoreUs = (
									dealContractValue + // contract
									silentCapotValue + // 'silent capot' 250 to Generale's base of 500
									pointsDoneUs + // stays inside
									pointsDoneThem // stays inside
								) * contraMult +
								belotePointsUs + // belote is not multiplied by 'coinche doubles all points'
								declPointsParsedUs; // declarations are not multiplied by 'coinche doubles all points'
								scoreThem = belotePointsThem + declPointsParsedThem;
							}
							else {
								scoreUs =
									dealContractValue * contraMult + // deal
									silentCapotValue +
									pointsDoneUs +
									pointsDoneThem +
									belotePointsUs +
									declPointsParsedUs;
									
								scoreThem = belotePointsThem + declPointsParsedThem;
							}
						}
						// we bid, we won, not contra-ed
						else {
							scoreUs = 
								dealContractValue + 
								silentCapotValue + 
								pointsDoneUs + 
								belotePointsUs + 
								declPointsParsedUs;
							scoreThem = pointsDoneThem + belotePointsThem + declPointsParsedThem;
						}
					}
				}
				// Belote
				// we attacked, we won
				else {
					hangingPointsUs = this.data.hangingPoints;
					hangingPointsThem = 0;
					scoreUs = pointsDoneUs + belotePointsUs + declPointsParsedUs + hangingPointsUs;
					scoreThem = pointsDoneThem + belotePointsThem + declPointsParsedThem + hangingPointsThem;
				}
			}
			// they are attacking and losing
			// (we are defending and winning)
			else {
				// if Coinche|Contree
				if (isContraVariation) {
					// they bid, they lost
					// if scoreContractOnly
					if (isScoreContractOnly) {
						// we get: their contract
						// they get: 0
						scoreUs = dealContractValue * contraMult;
						scoreThem = 0;
					}
					// not scoreContractOnly
					else {
						// they bid, they lost, got contra-ed.
						// - Contra awards all tricks to winner
						if (isContra) {
							// - we get: contract + our tricks + their tricks + our belote + our decl
							// - they get: their belote + their decl
							if (isContraDoublesAllPoints) {
								scoreUs = (
									dealContractValue + 
									silentCapotValue + 
									pointsDoneUs + 
									pointsDoneThem
								) * contraMult +
								belotePointsUs + 
								declPointsParsedUs;
								scoreThem = belotePointsThem + declPointsParsedThem;
							} else {
								scoreUs = 
									dealContractValue * contraMult + 
									silentCapotValue + 
									pointsDoneUs + 
									pointsDoneThem +
									belotePointsUs + 
									declPointsParsedUs;
								scoreThem = belotePointsThem + declPointsParsedThem;
							}
						}
						// they bid, they lost, not contra-ed
						else {
							// - we get: contract + our tricks + their tricks + our belote + our decl
							// - they get: their belote + their decl
							scoreUs = 
								dealContractValue + 
								silentCapotValue + 
								pointsDoneUs +
								pointsDoneThem + 
								belotePointsUs + 
								declPointsParsedUs;
							scoreThem = belotePointsThem + declPointsParsedThem;
						}
					}
				}
				// Belote
				// they attacked, they lost
				else {
					hangingPointsUs = this.data.hangingPoints;
					hangingPointsThem = 0;
					scoreUs = pointsDoneUs + belotePointsUs + declPointsParsedUs + pointsDoneThem + hangingPointsUs;
					scoreThem = belotePointsThem + declPointsParsedThem + hangingPointsThem;
				}
			}
			// reset hanging points (HP from previous Deal are cached/safe)
			this.data.hangingPoints = 0;
			this.data.numDealsWonUs += 1;
		}
		else if (this.dynamicData.dealWinningSide == PlayerSide.THEM) {
			// they are attacking and winning
			// (we are defending and losing)
			if (this.dynamicData.dealWinningSide == attackingSide) {
				// if Coinche|Contree
				if (isContraVariation) {
					// they bid, they won
					// if scoreContractOnly
					if (isScoreContractOnly) {
						// we get: 0
						// they get: contract
						scoreUs = 0;
						scoreThem = dealContractValue * contraMult;
					}
					// not scoreContractOnly
					else {
						// they bid, they won, got contra-ed.
						// - Contra awards all tricks to winner
						if (isContra) {
							// - we get: 0 contract + 0 tricks + our belote + our decl
							// - they get: contract + their tricks + our tricks + their belote + their decl
							if (isContraDoublesAllPoints) {
								scoreUs = belotePointsUs + declPointsParsedUs;
								scoreThem = (
									dealContractValue + 
									silentCapotValue + 
									pointsDoneThem + 
									pointsDoneUs
								) * contraMult +
								belotePointsThem + 
								declPointsParsedThem;
							} else {
								scoreUs = belotePointsUs + declPointsParsedUs;
								scoreThem = 
									dealContractValue * contraMult + 
									silentCapotValue + 
									pointsDoneThem + 
									pointsDoneUs +
									belotePointsThem + 
									declPointsParsedThem;
							}
						}
						// they bid, they won, not contra-ed
						else {
							// they get: contract + their tricks + their belote + their decl
							// we get: 0 contract + our tricks + our belote + our decl
							scoreUs = pointsDoneUs + belotePointsUs + declPointsParsedUs;
							scoreThem = dealContractValue + silentCapotValue + pointsDoneThem + belotePointsThem + declPointsParsedThem;
						}
					}
				}
				// Belote
				// they attacked, they won
				else {
					hangingPointsUs = 0;
					hangingPointsThem = this.data.hangingPoints;

					// default scoring: each team gets their points.
					// they get: their tricks + their belote + their decl + hangingPoints
					// we get: our tricks + our belote + our decl
					scoreUs = pointsDoneUs + belotePointsUs + declPointsParsedUs + hangingPointsUs;
					scoreThem = pointsDoneThem + belotePointsThem + declPointsParsedThem + hangingPointsThem;
				}
			}
			// they are defending and winning
			// (we are attacking and losing)
			else {
				// if Coinche|Contree
				if (isContraVariation) {
					// we bid, we lost
					// if scoreContractOnly
					if (isScoreContractOnly) {
						// we get: 0
						// they get: their contract
						scoreUs = 0;
						scoreThem = dealContractValue * contraMult + silentCapotValue;
					}
					// not scoreContractOnly
					else {
						// we bid, we lost, got contra-ed.
						// - Contra awards all tricks to winner
						if (isContra) {
							// - we get: 0 contract + 0 tricks + our belote + our decl
							// - they get: contract + their tricks + our tricks + their belote + their decl
							if (isContraDoublesAllPoints) {
								scoreUs = belotePointsUs + declPointsParsedUs;
								scoreThem = (
									dealContractValue + 
									silentCapotValue +
									pointsDoneThem +
									pointsDoneUs
								) * contraMult + 
								belotePointsThem +
								declPointsParsedThem;
							} else {
								scoreUs = belotePointsUs + declPointsParsedUs;
								scoreThem = 
									dealContractValue * contraMult + 
									silentCapotValue + 
									pointsDoneThem + 
									pointsDoneUs +
									belotePointsThem + 
									declPointsParsedThem;
							}
						}
						// we bid, we lost, not contra-ed
						else {
							// - we get: 0 contract + 0 tricks + our belote + our decl
							// - they get: contract + their tricks + our tricks + their belote + their decl
							scoreUs = belotePointsUs + declPointsParsedUs;
							scoreThem = 
								dealContractValue + 
								silentCapotValue + 
								pointsDoneThem + 
								pointsDoneUs +
								belotePointsThem + 
								declPointsParsedThem;
						}
					}
				}
				// Belote
				// we attacked, we lost
				else {
					hangingPointsUs = 0;
					hangingPointsThem = this.data.hangingPoints;
					scoreUs = belotePointsUs + declPointsParsedUs + hangingPointsUs;
					scoreThem = pointsDoneThem + belotePointsThem + declPointsParsedThem + pointsDoneUs + hangingPointsThem;
				}
			}
			// reset hanging points (HP from previous Deal are cached/safe)
			this.data.hangingPoints = 0;
			this.data.numDealsWonThem += 1;
		}
		// Deuced just now (hangingPoints=TRUE)
		else if (this.dynamicData.dealWinningSide == PlayerSide.NONE) {
			// Deuce:
			// - defenders: their tricks + belote + decl
			// - attackers: 'points' HANG to next Deal, are given then to winner.
			// Then move attacker's 'Total' points to hang until next round.
			// - Attacker will win it's post-Total Belote and Declaration (?check rules) points.
			// - Defender will win it's own points this round.
			// NOTE: hangingPoints and scoreContractOnly variation support do not mesh (so no reason to resolve for presence of both)
			if (attackingSide == PlayerSide.US) {
				scoreUs = belotePointsUs + declPointsParsedUs;
				scoreThem = pointsDoneThem + belotePointsThem + declPointsParsedThem;
				// add to hangingPoints for next round
				this.data.hangingPoints = pointsDoneUs;
			} else {
				scoreUs = pointsDoneUs + belotePointsUs + declPointsParsedUs;
				scoreThem = belotePointsThem + declPointsParsedThem;
				this.data.hangingPoints = pointsDoneThem;
			}
		}

		// ntatDoublePoints - if NT||AT was selected and ntatDoublePoints is togglend on,
		// then here we double scoreUs & scoreThem points
		if (isNTATDoublePoints && (this.data.trumpSuit == TrumpSuit.NOTRUMPS || this.data.trumpSuit === TrumpSuit.ALLTRUMPS)) {
			scoreUs *= 2;
			scoreThem *= 2;
		}

		// for Coinche|Contree, round Score to nearest 10
		if (isContraVariation) {
			scoreUs = Math.round(scoreUs / 10) * 10;
			scoreThem = Math.round(scoreThem / 10) * 10;
		}
		

		// update refs
		this.dynamicData.dealTotalScoreUs = scoreUs;
		this.dynamicData.dealTotalScoreThem = scoreThem;
		this.data.gameTotalScoreUs += scoreUs;
		this.data.gameTotalScoreThem += scoreThem;
		// console.log(scoreUs, scoreThem);

		// update scoreView and selectedTrumpView (size, layout/snapping)
		// from screen scale OR visibility change
		this.table.UpdateScoreAndSelectedTrumpViews();

		//
		// PARSE SCREEN DATA (all contents)
		//

		const resultsItems = [];
		const locSharedVariationItemsById = AppLocalization.GetVariationData(this.variationId).dealComplete.results.itemsById;

		// Trump
		let resultsItemData: ResultsListItemData;
		if (this.data.trumpSuit == TrumpSuit.NOTRUMPS) {
			// resultsItemData = "NT";
			resultsItemData = { label: AppLocalization.GetSharedData().general.noTrumpsShortText };
		} else if (this.data.trumpSuit == TrumpSuit.ALLTRUMPS) {
			// resultsItemData = "AT"
			resultsItemData = { label: AppLocalization.GetSharedData().general.allTrumpsShortText };
		}
		// else its a trumpSuit
		else {
			const cardSuit: CardSuit = TrumpSuitToCardSuit(this.data.trumpSuit);
			// use texturedata
			let resultsIconTextureStatesData = GetCardSuitIconTextureStatesData(cardSuit, TrumpIconType.RESULTS_SCREEN);
			if (resultsIconTextureStatesData.out == undefined) {
				// console.log("undefined for trumpSuit=", TrumpSuit[this.data.trumpSuit], " as cardSuit=", CardSuit[cardSuit], " as iconStatesData=", resultsIconTextureStatesData + ". Forcing hearts.");
				resultsIconTextureStatesData = {
					out: { texture: LoaderFileKeys.ui, frame: 'icon-results-hearts-out.png' },
				};
			}
			resultsItemData = { icon: resultsIconTextureStatesData.out };
		}

		// Contract (Coinche|Contree)
		let contractUs: string;
		let contractThem: string;
		if (isContraVariation) {
			const sResultsContractValue =
				AppLocalization.GetVariationData().dealComplete.results.itemsById['contract'].choices[this.data.bids.highestChoiceValueIndex];
			if (attackingSide == PlayerSide.US) {
				contractUs = sResultsContractValue;
				contractThem = '';
			} else {
				contractUs = '';
				contractThem = sResultsContractValue;
			}
		}

		// Contra (Coinche|Contree & only if Coinche|Surcoinche made)
		let contraUs: string = '';
		let contraThem: string = '';
		if (isContra) {
			let sContra = AppLocalization.GetVariationData().dealComplete.results.itemsById['contra'].choices[0];
			// we are attacker side and we have been contra-ed
			if (attackingSide == PlayerSide.THEM) {
				contraUs = sContra;
			} else {
				contraThem = sContra;
			}
			if (isRecontra) {
				let sRecontra = AppLocalization.GetVariationData().dealComplete.results.itemsById['contra'].choices[1];
				// we are defender side and we have recontra-ed
				if (defendingSide == PlayerSide.THEM) {
					contraUs = sRecontra;
				} else {
					contraThem = sRecontra;
				}
			}
		}

		// Belote
		const beloteData = {
			label1: locSharedVariationItemsById['belote'].itemLabel,
			label2: { label: belotePointsUs },
			label3: { label: belotePointsThem },
		};

		// Declarations
		const declarationsData = {
			label1: locSharedVariationItemsById['declarations'].itemLabel,
			label2: { label: this.data.dealMajorDeclarationPointsUs },
			label3: { label: this.data.dealMajorDeclarationPointsThem },
			// label2: { label: declPointsParsedUs },
			// label3: { label: declPointsParsedThem },
		};

		// [[ Result ]]
		// ([Done], [Failed] or [Hanging])
		// NOTE: [Hanging] state is based on winning/losing side being equal in Belote variation (with deuce enabled in settings). At this point this.data.hangingPoints has been reset to 0, IF we didnt DEUCE AGAIN in this Deal. Else, it is >0. In case this DEAL comes AFTER previous' Deal's DEUCE, and this.data.hangingPoints are 0 (no DEUCE this deal), then the hangingPoints are cached in @see hangingPointsUs and hangingPointsThem for display in special 'Hung' row item (@see followup calculations done for [Hung] row item).
		const contractResult: ContractResult = GetContractResult(this.dynamicData.dealWinningSide, attackingSide);
		let resultDataUs: ResultsListItemData;
		let resultDataThem: ResultsListItemData;
		// we won
		if (this.dynamicData.dealWinningSide == PlayerSide.US) {
			// our contract?
			if (attackingSide == PlayerSide.US) {
				resultDataUs = {
					label: AppLocalization.GetSharedData().dealComplete.results.contractResult.success,
					bubbleColor: ResultsListItemBubbleColor.GREEN,
				};
				resultDataThem = { label: '' };
			}
			// their contract?
			else if (attackingSide == PlayerSide.THEM) {
				resultDataUs = { label: '' };
				resultDataThem = {
					label: AppLocalization.GetSharedData().dealComplete.results.contractResult.failure,
					bubbleColor: ResultsListItemBubbleColor.RED,
				};
			}
		}
		// they won
		else if (this.dynamicData.dealWinningSide == PlayerSide.THEM) {
			// our contract?
			if (attackingSide == PlayerSide.US) {
				resultDataUs = {
					label: AppLocalization.GetSharedData().dealComplete.results.contractResult.failure,
					bubbleColor: ResultsListItemBubbleColor.RED,
				};
				resultDataThem = { label: '' };
			}
			// their contract?
			else if (attackingSide == PlayerSide.THEM) {
				resultDataUs = { label: '' };
				resultDataThem = {
					label: AppLocalization.GetSharedData().dealComplete.results.contractResult.success,
					bubbleColor: ResultsListItemBubbleColor.GREEN,
				};
			}
		}
		// Deuce! (and hangingPoints true in Options)
		else {
			// our contract?
			if (attackingSide == PlayerSide.US) {
				resultDataUs = {
					label: AppLocalization.GetSharedData().dealComplete.results.contractResult.hanging,
					// bubbleColor: ResultsListItemBubbleColor.YELLOW,
					bubbleColor: ResultsListItemBubbleColor.ORANGE,
				};
				resultDataThem = { label: '' };
			}
			// their contract?
			else if (attackingSide == PlayerSide.THEM) {
				resultDataUs = { label: '' };
				resultDataThem = {
					label: AppLocalization.GetSharedData().dealComplete.results.contractResult.hanging,
					// bubbleColor: ResultsListItemBubbleColor.YELLOW,
					bubbleColor: ResultsListItemBubbleColor.ORANGE,
				};
			}
		}

		// [[ historyDealsData ]]
		// Coinche|Contree
		if (isContra) {
			// add deal to history deals data
			let dealsDealContractValue: string =
				AppLocalization.GetVariationData().dealComplete.deals.deal.contract.choices[this.data.bids.highestChoiceValueIndex];
			let dealsDealContraValue: string; // undefined is ACCEPTED useful value
			// recontra
			if (isRecontra) {
				dealsDealContraValue = AppLocalization.GetVariationData().dealComplete.deals.deal.contra.choices[1];
			}
			// contra
			else {
				dealsDealContraValue = AppLocalization.GetVariationData().dealComplete.deals.deal.contra.choices[0];
			}
			this.data.historyDealsData.push({
				attack: {
					// eg "John" or "${PLAYER_NAME}" etc
					playerName: this.table.players.GetPlayerByIndex(this.data.playerAttackerIndex).GetName(),
					// eg 0 ('North') or 1 ('East') etc
					axisIndex: this.data.playerAttackerIndex,
				},
				contract: {
					label1: dealsDealContractValue,
					trumpSuit: this.data.trumpSuit,
					label2: dealsDealContraValue,
				},
				points: {
					// us: dealPointsUs,
					// them: dealPointsThem,
					us: totalUs,
					them: totalThem,
				},
				result: contractResult,
				score: {
					us: scoreUs,
					them: scoreThem,
				},
			});
		}
		// Belote
		else {
			// add deal to history deals data
			this.data.historyDealsData.push({
				attack: {
					// eg "John" or "${PLAYER_NAME}" etc
					playerName: this.table.players.GetPlayerByIndex(this.data.playerAttackerIndex).GetName(),
					// eg 0 ('North') or 1 ('East') etc
					axisIndex: this.data.playerAttackerIndex,
				},
				contract: { trumpSuit: this.data.trumpSuit },
				points: {
					// us: dealPointsUs,
					// them: dealPointsThem,
					us: totalUs,
					them: totalThem,
				},
				result: contractResult,
				score: { us: scoreUs, them: scoreThem },
			});
		}

		/**
		 * RULES regarding (1) transition to next Deal and (2) Hanging points
		 * - Deuce / hangingPoints / isUseHangingPoints has NO CONTRIBUTION in transitioning to NEXT TABLE / Deal
		 * - Hanging points trigger naturally when DEAL totalUs and totalThem is EQUAL 
		 *   eg when both teams have same amount of total deal points
		 * - When this happens, this.dynamicData.dealWinningSide is assigned PlayerSide.NONE
		 * - By default (can be changed via layout JSON), Deuce, and thus the Hanging Points calculation 
		 *   only pertains to Belote variation, and even then, only if enabled in Options
		 * - The only 2 factors that can transition table to next Deal from DealCompleteScreen are:
		 *   (1) no team has gone over pointsPerGame yet with their gameTotalScore (gameTotalScoreUs/gameTotalScoreThem)
		 *       so naturally, we go next Deal until gameTotalScore goes over gameTotalScore for any team.
		 *   (2) in rare case of BOTH teams going over pointsPerGame with their gameTotalScores over last Deal 
		 *       and having SAME amount of points, then Deals:Us/Them bubble need be marked RED 
		 *       and table can continue to next Deal, at the end of which same checks are performed etc 
		 *       until both teams have different gameTotalScores (and their Deals:Us/Them bubbles are colored 1 green and 1 red).
		 *   Note, that in a super-rare case of BOTH teams going over pointsPerGame with their gameTotalScores over last Deal, where there are also Hanged Points in current Deal,
		 *   these Hanged points are IGNORED / DISCARDED.
		 */

		const pointsPerGame: number = OptionsDataManager.GetPointsPerGame();
		let gameCompleteViewLayout: GameCompleteViewLayout = GameCompleteViewLayout.NONE;
		let gtmCompleteDealOrGame: GTMTrackingCompleteDealOrGame = GTMTrackingCompleteDealOrGame.DEAL;
		let totalScoreBubbleColorUs: ResultsListItemBubbleColor;
		let totalScoreBubbleColorThem: ResultsListItemBubbleColor;
		const reachedPointsPerGame = this.data.gameTotalScoreUs >= pointsPerGame || this.data.gameTotalScoreThem >= pointsPerGame;
		// if a tie (on level of Game Totals)
		if (this.data.gameTotalScoreUs == this.data.gameTotalScoreThem) {
			totalScoreBubbleColorUs = ResultsListItemBubbleColor.ORANGE;
			totalScoreBubbleColorThem = ResultsListItemBubbleColor.ORANGE;
		}
		// if our Game Totals bigger than theirs
		else
		if (this.data.gameTotalScoreUs > this.data.gameTotalScoreThem) {
			if (reachedPointsPerGame) {
				gameCompleteViewLayout = GameCompleteViewLayout.WIN;
				gtmCompleteDealOrGame = GTMTrackingCompleteDealOrGame.GAME;
			}
			totalScoreBubbleColorUs = ResultsListItemBubbleColor.GREEN;
			totalScoreBubbleColorThem = ResultsListItemBubbleColor.RED;
		}
		// if their Game Totals bigger than ours
		else {
			if (reachedPointsPerGame) {
				gameCompleteViewLayout = GameCompleteViewLayout.LOSS;
				gtmCompleteDealOrGame = GTMTrackingCompleteDealOrGame.GAME;
			}
			totalScoreBubbleColorUs = ResultsListItemBubbleColor.RED;
			totalScoreBubbleColorThem = ResultsListItemBubbleColor.GREEN;
		}
		// console.log(gameWonUs, gameWonThem);
		// DEBUG
		// gameCompleteViewLayout = GameCompleteViewLayout.NONE;

		//
		// SET PARSED SCREEN DATA (all contents)
		//

		// Results / Trump
		resultsItems.push({
			label1: locSharedVariationItemsById['trump'].itemLabel,
			label2: attackingSide == PlayerSide.US ? resultsItemData : { label: '' },
			label3: attackingSide == PlayerSide.THEM ? resultsItemData : { label: '' },
		});

		// Results / Contract (Coinche|Contree)
		if (isContraVariation) {
			resultsItems.push({
				label1: locSharedVariationItemsById['contract'].itemLabel,
				label2: { label: contractUs },
				label3: { label: contractThem },
			});
		}

		// Results / Contra (Coinche|Contree & only if Coinche|Surcoinche made)
		if (isContra) {
			resultsItems.push({
				label1: locSharedVariationItemsById['contra'].itemLabel,
				label2: { label: contraUs },
				label3: { label: contraThem },
			});
		}

		// Results / Points done
		resultsItems.push({
			label1: locSharedVariationItemsById['pointsDone'].itemLabel,
			label2: { label: pointsDoneUs },
			label3: { label: pointsDoneThem },
		});

		// Results / Belote (IF beloteCountsForContractWinLoss=TRUE)
		if (isBeloteCountsForContractWinLoss) {
			resultsItems.push(beloteData);
		}

		// Results / Declarations (IF playWithDeclarations=TRUE && declarationsCountForContractWinLoss=TRUE)
		if (isPlayWithDeclarations) {
			if (isDeclarationsCountForContractWinLoss) {
				resultsItems.push(declarationsData);
			}
		}

		// Results / Hung (IF useHangingPoints=TRUE && hangingPoints != 0)
		// NOTE: [Hanging] state is based on winning/losing side being equal in Belote variation (with deuce enabled in settings). At this point this.data.hangingPoints has been reset to 0, IF we didnt DEUCE AGAIN in this Deal. Else, it is >0. In case this DEAL comes AFTER previous' Deal's DEUCE, and this.data.hangingPoints are 0 (no DEUCE this deal), then the hangingPoints are cached in @see hangingPointsUs and hangingPointsThem for display in the following 'Hung' row item.
		if (hangingPointsUs != 0 || hangingPointsThem != 0) {
			resultsItems.push({
				label1: locSharedVariationItemsById['hung'].itemLabel, // "Hung" - from prev deal.
				label2: { label: hangingPointsUs },
				label3: { label: hangingPointsThem },
			});
		}

		// Results / Total
		resultsItems.push({
			label1: locSharedVariationItemsById['total'].itemLabel,
			label2: { label: totalUs },
			label3: { label: totalThem },
		});

		// Results / Result
		// NOTE: Result state of [Hanging] - orange in color, is possible here also. @see resultDataUs and resultDataThem calculation
		resultsItems.push({
			label1: locSharedVariationItemsById['result'].itemLabel,
			label2: resultDataUs,
			label3: resultDataThem,
		});

		// Results / Belote (IF beloteCountsForContractWinLoss=FALSE)
		if (!isBeloteCountsForContractWinLoss) {
			resultsItems.push(beloteData);
		}

		// Results / Declarations (IF playWithDeclarations=TRUE && declarationsCountForContractWinLoss=FA:SE)
		if (isPlayWithDeclarations) {
			if (!isDeclarationsCountForContractWinLoss) {
				resultsItems.push(declarationsData);
			}
		}

		// Results / Score
		resultsItems.push({
			label1: locSharedVariationItemsById['score'].itemLabel,
			label2: { label: scoreUs },
			label3: { label: scoreThem },
		});

		// Results / Total score
		resultsItems.push({
			label1: locSharedVariationItemsById['totalScore'].itemLabel,
			label2: { label: this.data.gameTotalScoreUs, bubbleColor: totalScoreBubbleColorUs },
			label3: { label: this.data.gameTotalScoreThem, bubbleColor: totalScoreBubbleColorThem },
		});

		// RESULTS content: set data
		(this.table.GetScene().dealCompleteScreen.contentsScrollPane.GetContent(DealCompleteContentId.RESULTS) as DealCompleteResultsContent).SetData({
			gameCompleteViewLayout: gameCompleteViewLayout,
			// category title and first row of labels (label1 from each list item)
			// are excluded from dynamic data [because they are static localized texts]
			items: resultsItems,
		});

		// DEALS content: set data
		(this.table.GetScene().dealCompleteScreen.contentsScrollPane.GetContent(DealCompleteContentId.DEALS) as DealCompleteDealsContent).SetData({
			// category title and first row of labels (label1 from each list item)
			// are excluded from dynamic data [because they are static localized texts]
			items: this.data.historyDealsData,
			scoreUs: this.data.gameTotalScoreUs,
			scoreThem: this.data.gameTotalScoreThem,
			totalScoreBubbleColorUs: totalScoreBubbleColorUs,
			totalScoreBubbleColorThem: totalScoreBubbleColorThem
		});

		// TRICKS content: set data
		(this.table.GetScene().dealCompleteScreen.contentsScrollPane.GetContent(DealCompleteContentId.TRICKS) as DealCompleteTricksContent).SetData(
			this.data.historyTricksData
		);

		// HANDS content: set data
		(this.table.GetScene().dealCompleteScreen.contentsScrollPane.GetContent(DealCompleteContentId.HANDS) as DealCompleteHandsContent).SetData(
			this.data.historyHandsData.map((handCards, playerIndex) => {
				const itemLabel: string =
					this.table.players.GetPlayerByIndex(playerIndex).GetName() + ' (' + AppLocalization.GetSharedData().general.playersAxes[playerIndex] + ')';
				const parsedLabel: string = itemLabel.replace(
					Players.PLAYER_NAME,
					AppLocalization.GetSharedData(AppLocalization.GetCurLanguageId()).general.playerName
				);

				return {
					title: parsedLabel,
					cards: handCards,
				};
			})
		);

		// update static title and content titles (if needs update)
		const languageId: LanguageId = AppLocalization.GetCurLanguageId();
		// force set dirty forces refresh size (based on gameOverType)
		this.table.GetScene().dealCompleteScreen.SetData(this.variationId, languageId, gameCompleteViewLayout);

		// we are in: TaskStart(COMPLETE_DEAL)
		// deal complete, wait on table for a second, before showing dealComplete screen
		let dealCompleteScreenDelay: number = this.dynamicData.dealCompleteShowing
			? 0
			: OptionsDataManager.GetTiming(AppTimings.preDealCompleteScreenDelay);
		// DEBUG
		// dealCompleteScreenDelay = 1000;
		this.table.scene.time.delayedCall(dealCompleteScreenDelay, () => {
			// set dealCompleteScreen data and
			this.table.GetScene().dealCompleteScreen.SetTaskData({
				taskType: taskType,
				onComplete: this.OnTaskComplete.bind(this),
			});

			// const animateTableFadeout = this.dynamicData.enteredFromLobby;
			// const animateDealCompleteFadeIn = !this.dynamicData.restoreFromCache && !this.dynamicData.dealCompleteShowing;

			// NOTE: dynamicData is set on table entry! (new table, continue, restrore from cache [debug or legit tableData])
			let animateTableFadeout: boolean;
			let animateDealCompleteFadeIn: boolean;
			// restored from cache
			if (this.dynamicData.restoreFromCache) {
				// entered from lobby ("CONTINUE" from Lobby)
				if (this.dynamicData.enteredFromLobby) {
					// deal complete showing
					if (this.dynamicData.dealCompleteShowing) {
						animateTableFadeout = true;
						animateDealCompleteFadeIn = true;
					}
					// deal complete not showing (Just play Deal naturally from recovered state to finish)
					else {
						animateTableFadeout = true;
						animateDealCompleteFadeIn = true;
					}
				}
				// not entered from lobby (from RELOAD with existing tableData [or from debug data])
				else {
					// deal complete showing (insta show DealCompleteScreen)
					if (this.dynamicData.dealCompleteShowing) {
						animateTableFadeout = false;
						animateDealCompleteFadeIn = false;
					}
					// deal complete not showing (Just play Deal naturally from recovered state to finish)
					else {
						animateTableFadeout = true;
						animateDealCompleteFadeIn = true;
					}
				}
			}
			// not restored from cache
			else {
				// entered from lobby ("NEW GAME" from Lobby)
				if (this.dynamicData.enteredFromLobby) {
					// deal complete showing (UNREACHABLE state)
					if (this.dynamicData.dealCompleteShowing) {
						animateTableFadeout = false;
						animateDealCompleteFadeIn = false;
					}
					// deal complete not showing (Just play Deal naturally from start to finish)
					else {
						animateTableFadeout = true;
						animateDealCompleteFadeIn = true;
					}
				}
				// not entered from lobby ("CONTINUE" on DealCompleteScreen in previous Deal, at the end of next Deal)
				else {
					// deal complete showing (UNREACHABLE state)
					if (this.dynamicData.dealCompleteShowing) {
						animateTableFadeout = false;
						animateDealCompleteFadeIn = false;
					}
					// deal complete not showing (Just play second Deal naturally from start to finish)
					else {
						animateTableFadeout = true;
						animateDealCompleteFadeIn = true;
					}
				}
			}
			// console.log(this.dynamicData.restoreFromCache, this.dynamicData.enteredFromLobby, this.dynamicData.dealCompleteShowing);
			// console.log(animateTableFadeout, animateDealCompleteFadeIn);

			// track level completion on each deal end
			// first time (playing naturally) dealCompleteShowing is false.
			// then from restore (reload or go lobby and back at DealComplete screen) its true
			if (!this.dynamicData.dealCompleteShowing) {
				let gtmWinStatus: string = GTMTrackingDealWinOrLoss.TIE;
				if (this.dynamicData.dealWinningSide == PlayerSide.US) {
					gtmWinStatus = GTMTrackingDealWinOrLoss.WIN;
				} else if (this.dynamicData.dealWinningSide == PlayerSide.THEM) {
					gtmWinStatus = GTMTrackingDealWinOrLoss.LOSS;
				}
				// "scoreUs,scoreThem,gameTotalScoreUs,gameTotalScoreThem"
				const score: string = scoreUs + ',' + scoreThem + ',' + this.data.gameTotalScoreUs + ',' + this.data.gameTotalScoreThem;
				// console.log(score);
				GTMTracking.PLDTrackingLevelCompletion(score, 0, gtmWinStatus, gtmCompleteDealOrGame);
			}

			this.table.GetScene().tableScreen.transitionDirection = ScreenTransitionDirection.CENTER_TO_LEFT;
			this.table.GetScene().tableScreen.Hide(animateTableFadeout);
			this.table.GetScene().dealCompleteScreen.transitionDirection = ScreenTransitionDirection.RIGHT_TO_CENTER;
			this.table.GetScene().dealCompleteScreen.Show(animateDealCompleteFadeIn);
		});
	}

	public OnTaskComplete(taskType: number, taskData: any = undefined): void {
		// override in subclass
	}

	/**
	 * Adds delay between RESOLVE_PLAY_CARD and DEAL_PLAYER_TO_TABLE
	 * - Made into function due to frequent code reuse
	 */
	protected ResolvePlayCard(taskType: number, taskSubType: number): void {
		// continue RESOLVE_PLAY_CARD -> DEAL_PLAYER_TO_TABLE
		const postTrickCollectDelay = this.dynamicData.dealCompleteShowing ? 0 : OptionsDataManager.GetTiming(AppTimings.postTrickCollectDelay);
		// complete: COLLECT_CARDS
		this.table.scene.time.delayedCall(postTrickCollectDelay, () => {
			// capture restore point
			this.data.state = {
				// taskType: TaskTypeCoinche.RESOLVE_PLAY_CARD,
				// taskSubType: TaskSubTypeCoinche.PLAY_PHASE,
				taskType: taskType,
				taskSubType: taskSubType,
				// taskType: this.data.state.taskType,
				// taskSubType: this.data.state.taskSubType,
				taskData: {
					playerIndex: this.data.trickDealerIndex,
				},
			};
			// save restore point immediately to localStorage
			AppLocalStorage.SetTableData(this.variationId, this.data);
			// start task
			this.TaskStart(this.data.state.taskType, {
				playerIndex: this.data.state.taskData.playerIndex,
			});
		});
	}

	/**
	 * Is contract for this game a Capot contract? (Coinche|Contree only)
	 */
	protected IsContractCapot(): boolean {
		if (!this.IsContraVariation()) {
			return false;
		}
		return this.data.bids.highestChoiceValueIndex === AppLocalization.GetVariationData(this.variationId).table?.bidPopup.slider.capot.choiceIndex;
	}

	/**
	 * Is contract for this game a Generale contract? (Coinche|Contree only)
	 */
	protected IsContractGenerale(): boolean {
		if (!this.IsContraVariation()) {
			return false;
		}
		return this.data.bids.highestChoiceValueIndex === AppLocalization.GetVariationData(this.variationId).table?.bidPopup.slider.generale.choiceIndex;
	}

	/**
	 * Did single team take all tricks of a table (made a Capot?)
	 */
	protected IsCapot(): boolean {
		return this.data.curDealScoreUs === 0 || this.data.curDealScoreThem === 0;
	}

	/**
	 * Did single player take all tricks of a table (made a Generale?)
	 */
	protected IsGenerale(): boolean {
		const winnerIndex: number = this.data.historyTricksData[0].winnerIndex;
		for (let i = 1; i < this.data.historyTricksData.length; i++) {
			const trickData = this.data.historyTricksData[i];
			if (trickData.winnerIndex != winnerIndex) {
				return false;
			}
		}
		return true;
	}

	protected IsContraVariation(): boolean {
		// return this.data.bids && this.data.bids.highestChoiceValueIndex != -1;
		return this.variationId == VariationId.COINCHE || this.variationId == VariationId.CONTREE;
	}

	/**
	 * Modulo supplied index to be always within bounds ok players array
	 */
	public GetFixedPlayerIndex(index: number): number {
		const numPlayers: number = this.table.players.GetLength();
		return (numPlayers + index) % numPlayers;
	}

	/**
	 * Are we in bidding phase or in play phase?
	 *
	 * Coinche and Contree use this, so that when player leaves to Lobby and comes back, trump is 'not' shown by TrumpView (because each player bidding on a trump sets tableData.trumpSuit and that shows in trumpView on Table re-entry while still in bidding phase -- It should not!). Belote is easy to implement, but Belote doesnt use this function
	 */
	public GetTablePhase(): TablePhase {
		return TablePhase.PLAY;

		// override in subclass
	}

	/**
	 * Deal complete. Parse and Save variation statistics.
	 */
	protected SaveStatsDealComplete(): void {
		if (!AppDebug.CanSaveTable()) {
			return;
		}

		// console.log("SaveStatsDealComplete");

		// refs
		const locVariationData = AppLocalization.GetVariationData();
		// const playerSide: PlayerSide = GetPlayerSide(Players.PLAYER_INDEX); // simply: PlayerSide.US
		const attackingSide: PlayerSide = GetPlayerSide(this.data.playerAttackerIndex);
		const defendingSide: PlayerSide = GetOppositePlayerSide(attackingSide);
		const isContraTable = this.variationId != VariationId.BELOTE;
		// contra|recontra 'called'
		const isContra: boolean = this.data.contraPlayerIndex != -1;
		const isRecontra: boolean = this.data.recontraPlayerIndex != -1;

		// stats
		// numDealsPlayed: add 1
		StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numDealsPlayed, 1);

		// numAttackDealsPlayer
		// numAttackDealsWonPlayer
		if (this.data.playerAttackerIndex == Players.PLAYER_INDEX) {
			StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numAttackDealsPlayer, 1);
			if (attackingSide == this.dynamicData.dealWinningSide) {
				StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numAttackDealsWonPlayer, 1);
			}
		}

		// numAttackDealsUs
		// numAttackDealsWonUs
		if (attackingSide == PlayerSide.US) {
			StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numAttackDealsUs, 1);
			if (attackingSide == this.dynamicData.dealWinningSide) {
				StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numAttackDealsWonUs, 1);
			}
		}

		// numDefenseDealsUs
		// numDefenseDealsWonUs
		if (defendingSide == PlayerSide.US) {
			StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numDefenseDealsUs, 1);
			if (defendingSide == this.dynamicData.dealWinningSide) {
				StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numDefenseDealsWonUs, 1);
			}
		}

		// totalScoreUs
		StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.totalScoreUs, this.dynamicData.dealTotalScoreUs);

		// totalScoreThem
		StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.totalScoreThem, this.dynamicData.dealTotalScoreThem);

		// we won deal
		if (this.dynamicData.dealWinningSide == PlayerSide.US) {
			StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numDealsWonUs, 1);
		}

		// someone made capot (announced or unannounced)
		if (this.IsCapot()) {
			// we won deal
			if (this.dynamicData.dealWinningSide == PlayerSide.US) {
				// capots won by us += 1
				StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numCapotsWonUs, 1);
			}
		}

		// variation specific: Coinche|Contree only
		if (isContraTable) {
			const dealContractValue: number =
				AppLocalization.GetVariationData().table.bidPopup.slider.choicesContractValue[this.data.bids.highestChoiceValueIndex];
			// console.log(this.data.bids."contractAsInt=", parseInt(dealContractValue));

			// valueContractsUs
			if (attackingSide == PlayerSide.US) {
				StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.valueContractsUs, dealContractValue);
			}

			// contract is capot
			if (this.IsContractCapot()) {
				// we announced Capot
				if (attackingSide == PlayerSide.US) {
					// num announced capots by us += 1
					StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numCapotsCalledUs, 1);
				}
			}

			// contract is Generale
			if (this.IsContractGenerale()) {
				// we announced Generale
				if (attackingSide == PlayerSide.US) {
					// num announced generales by us += 1
					StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numGeneralesCalledUs, 1);
				}
			}

			// someone made Generale (announced or unannounced)
			if (this.IsGenerale()) {
				// we won deal
				if (this.dynamicData.dealWinningSide == PlayerSide.US) {
					// Generales won by us += 1
					StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numGeneralesWonUs, 1);
				}
			}

			// Contra & Recontra calc
			// ----------------------
			// 'called'
			// > +1 to the contra/recontra to the team that called it
			// > 1 (contra) or 2 (contra+recontra) 'called' stats per Deal
			// ----------------------
			// 'won'
			// > +1 to HIGHEST type (contra/recontra) ONLY
			//   (if recontra is called, no side 'wins' contra)
			// > 0 (none) or 1 (contra or recontra) 'won' stats per Deal

			// contra was called
			if (isContra) {
				// we called contra
				if (defendingSide == PlayerSide.US) {
					// num contras called by us += 1
					StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numContrasCalledUs, 1);
				}
				// they called contra
				else if (defendingSide == PlayerSide.THEM) {
					// num contras called by them += 1
					StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numContrasCalledThem, 1);
				}

				// recontra was NOT called (else recontra is won, not contra)
				if (!isRecontra) {
					// whoever won deal, won contra
					// we won deal
					if (this.dynamicData.dealWinningSide == PlayerSide.US) {
						// num contras won by us += 1
						StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numContrasWonUs, 1);
					}
					// they won deal
					else if (this.dynamicData.dealWinningSide == PlayerSide.THEM) {
						// num contras won by them += 1
						StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numContrasWonThem, 1);
					}
				}
			}

			// recontra was called
			if (isRecontra) {
				// we called recontra
				if (attackingSide == PlayerSide.US) {
					// num recontras called by us += 1
					StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numReContrasCalledUs, 1);
				}

				// whoever won deal, won recontra
				// we won deal
				if (this.dynamicData.dealWinningSide == PlayerSide.US) {
					// num recontras won by us += 1
					StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numReContrasWonUs, 1);
				}
				// else .. (no such statistic)
			}
		}

		StatisticsDataManager.SaveVariationData();
		this.table.GetScene().statisticsScreen.SetDirty();
	}

	protected SaveStatsGameComplete(): void {
		// console.log("SaveStatsGameComplete");

		// refs
		const gameWinningSide: PlayerSide = this.data.gameTotalScoreUs > this.data.gameTotalScoreThem ? PlayerSide.US : PlayerSide.THEM;

		// numGamesPlayed
		StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numGamesPlayed, 1);

		// numGamesWonUs
		if (gameWinningSide == PlayerSide.US) {
			// cache.numGamesWonUs += 1
			StatisticsDataManager.SetIntermediateStatByKey(IntermediateStatsItemKeys.numGamesWonUs, 1);
		}

		StatisticsDataManager.SaveVariationData();
		this.table.GetScene().statisticsScreen.SetDirty();
	}

	/**
	 * Add
	 * A) major declaration or
	 * B) belote/rebelote
	 * bonus points to tableData
	 * @param declaration
	 */
	public AddDealBonusPoints(declaration: PlayerDeclaration): void {
		const playerSide: PlayerSide = GetPlayerSide(declaration.playerIndex);
		if (playerSide == PlayerSide.US) {
			if (declaration.declarationType == DeclarationType.BELOTE_REBELOTE) {
				this.data.dealBelotePointsUs += declaration.declarationValue;
			} else {
				this.data.dealMajorDeclarationPointsUs += declaration.declarationValue;
			}
		}
		// THEM
		else {
			if (declaration.declarationType == DeclarationType.BELOTE_REBELOTE) {
				this.data.dealBelotePointsThem += declaration.declarationValue;
			} else {
				this.data.dealMajorDeclarationPointsThem += declaration.declarationValue;
			}
		}
	}
}
